/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Typography,
  Divider,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { signIn } from "../actions/auth.action";
import SelectCompany from "../components/SelectCompany";
import logo from "../assets/img/s10Logo.png";
import MyTextField from "../components/MyTextField";
import authImage from '../assets/img/auth.png';

const theme = createTheme({
  palette: {
    primary: {
      main: "#10617B",
    },
    background: {
      default: "#f5f5f5",
    },
  },
});

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    margin: 0,
  },
  leftColumn: {
    backgroundColor: "#E8F3F7",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  formContainer: {
    maxWidth: "320px",
    width: "100%",
  },
  logo: {
    width: "140px",
    height:  "140px",
    marginBottom: '.3rem'
  },
  title: {
    color: "#3C8DBD", 
    fontSize: '32px', 
    fontWeight: 700,
    marginBottom: '0.5rem',
    lineHeight: "1.167",
    letterSpacing: "0em",
  },
  subTitle: {
    color: "black", 
    margin: '0',
    letterSpacing: "0.0075em",
    fontFamily: "Roboto",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  divider: {
    width: "100%",
    margin: "1.5rem 0",
    backgroundColor: theme.palette.grey[300],
  },
  button: {
    backgroundColor: '#10617B',
    marginTop: '3rem'
  },
  rightColumn: {
    backgroundImage: `url(${require("../assets/img/loginDefaultImage.svg").default})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundColor: theme.palette.primary.dark,
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "block",
    },
  },
  loadingOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255,255,255,0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1300,
  },
}));

export default function SignIn() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const rememberedUsername = localStorage.getItem("rememberedUsername");
    if (rememberedUsername) {
      formik.setFieldValue("UserName", rememberedUsername);
      formik.setFieldValue("rememberMe", true);
    }
  }, []);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleShowCompanyModal = () => {
    setShowCompanyModal(true);
  };

  const formik = useFormik({
    initialValues: {
      UserName: localStorage.getItem("rememberedUsername") || "",
      Password: "",
      rememberMe: !!localStorage.getItem("rememberedUsername"),
    },
    validationSchema: Yup.object({
      UserName: Yup.string()
        .email("El email no es válido")
        .required("El Email es Obligatorio"),
      Password: Yup.string().required("El password no puede ir vacío"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      if (values.rememberMe) {
        localStorage.setItem("rememberedUsername", values.UserName);
      } else {
        localStorage.removeItem("rememberedUsername");
      }
      await dispatch(signIn(values, handleShowCompanyModal));
      setLoading(false);
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Grid container className={classes.root}>
        <Grid item xs={12} md={6} className={classes.leftColumn}>
          <div className={classes.formContainer}>
            <img src={logo} alt="Logo" className={classes.logo}  />
            <Typography variant="h3" className={classes.title}>
              ¡Bienvenido!
            </Typography>
            <Typography variant="h6" className={classes.subTitle} >
              Portal Tablero - Ingresar
            </Typography>
            <Divider className={classes.divider} />
            <Typography variant="body1" align="left" style={{ width: "100%", marginBottom: '1rem', fontSize: '12px' }}>
              Ingresa tus credenciales
            </Typography>

            <form onSubmit={formik.handleSubmit}>


              <MyTextField
                id="username"
                label="Usuario"
                type="text"
                name="UserName"
                value={formik.values.UserName}
                onChange={formik.handleChange}
                error={formik.touched.UserName && Boolean(formik.errors.UserName)}
                helperText={formik.touched.UserName && formik.errors.UserName}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" style={{ color: "#191919" }}>
                      <i className="fas fa-envelope" style={{ color: "#999", fontSize: '1.5rem' }}></i>
                    </InputAdornment>
                  ),
                }}
              />
              
              <Grid item style={{marginTop: '1rem'}}>
                <MyTextField
                  id="Password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  name="Password"
                  value={formik.values.Password}
                  onChange={formik.handleChange}
                  error={formik.values.Password.length > 3 && Boolean(formik.errors.Password)}
                  helperText={formik.values.Password.length > 3 ? formik.errors.Password : ""}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={togglePasswordVisibility} edge="end">
                          {showPassword 
                          ? <Visibility style={{ color: "#999", fontSize: '1.5rem' }} /> 
                          : <VisibilityOff style={{ color: "#999", fontSize: '1.5rem' }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>


              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.rememberMe}
                    onChange={(e) => formik.setFieldValue("rememberMe", e.target.checked)}
                    color="primary"
                  />
                }
                label="Recordarme"
              />

              <Typography variant="body2" style={{ marginTop: "10px" }}>
                ¿Olvidaste tu contraseña?{" "}
                <a href="/recuperar-contrasena" color="primary">
                  Recupérala aquí
                </a>
              </Typography>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                className={classes.button}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Iniciar sesión"}
              </Button>
            </form>
          </div>
        </Grid>

        <Grid item md={6} className={classes.rightColumn}>
          <Box
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 0,
            }}
          >
            <img
              src={authImage}
              alt="Ilustración auth"
              style={{
                maxWidth: '100%',
                maxHeight: '90%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

        </Grid>
      </Grid>

      <SelectCompany setShow={setShowCompanyModal} show={showCompanyModal} />

      {loading && (
        <div className={classes.loadingOverlay}>
          <CircularProgress />
        </div>
      )}
    </ThemeProvider>
  );
}